import type { Signal } from '@preact/signals-react'
import { signal } from '@preact/signals-react'
import type { Product } from '@susu/headless-commerce/features/configurator/domain/Product/Product'
import type { Step } from '@susu/headless-commerce/features/configurator/domain/Step/Step'
import type { MediaWrapperV2 } from '@susu/headless-commerce/gql/generated/graphql'
import { isNotUndefined, isUndefined } from '@susu/undefined'
import type { Campaign } from '../Campaign/Campaign'
import type { Configurator } from '../Configurator/Configurator'
import type { ProductIdFilter } from '../Filter/ProductIdFilter'
import type { ProductIdOverrideFilter } from '../Filter/ProductIdOverrideFilter'
import type { Selected } from '../Selection/Selection'

export class Option {
  campaignId: Signal<string | undefined>
  configurator: Signal<Configurator>
  previewSelectionOverride: Signal<Array<ProductIdOverrideFilter>>
  product: Signal<Product>
  recommendations: Signal<Selected>
  selectionOverride: Signal<Array<ProductIdOverrideFilter>>
  step: Signal<Step>
  thumbnailImage: Signal<MediaWrapperV2>
  visibility: Signal<Array<ProductIdFilter>>

  constructor(
    campaignId: string | undefined,
    configurator: Configurator,
    previewSelectionOverride: Array<ProductIdOverrideFilter>,
    product: Product,
    recommendations: Selected,
    selectionOverride: Array<ProductIdOverrideFilter>,
    step: Step,
    thumbnailImage: MediaWrapperV2,
    visibility: Array<ProductIdFilter>,
  ) {
    this.campaignId = signal(campaignId)
    this.configurator = signal(configurator)
    this.previewSelectionOverride = signal(previewSelectionOverride)
    this.product = signal(product)
    this.recommendations = signal(recommendations)
    this.selectionOverride = signal(selectionOverride)
    this.step = signal(step)
    this.thumbnailImage = signal(thumbnailImage)
    this.visibility = signal(visibility)
  }

  isVisible() {
    if (this.visibility.value.length === 0) {
      return true
    }

    let visible = false
    for (const filter of this.visibility.value) {
      if (filter.isTrue(this.configurator.value)) {
        visible = true
        break
      }
    }
    return visible
  }

  hasSelectionOverride() {
    return this.selectionOverride.value.length > 0
  }

  getSelectionOverrides() {
    if (!this.hasSelectionOverride()) {
      return []
    }
    const allSteps: Array<Step['name']['value']> = []
    for (const filter of this.selectionOverride.value) {
      if (!filter.isTrue(this.configurator.value)) {
        continue
      }
      const matchedSteps = filter.matchedSteps(this.configurator.value)
      if (matchedSteps.length === 0) {
        continue
      }
      for (const step of matchedSteps) {
        allSteps.push(step)
      }
    }
    return allSteps
  }

  hasPreviewSelectionOverride() {
    return this.previewSelectionOverride.value.length > 0
  }

  getPreviewSelectionOverrides() {
    if (!this.hasPreviewSelectionOverride()) {
      return []
    }
    const allSteps: Array<Step['name']['value']> = []
    for (const filter of this.previewSelectionOverride.value) {
      if (!filter.isTrue(this.configurator.value)) {
        continue
      }
      const matchedSteps = filter.matchedSteps(this.configurator.value)
      if (matchedSteps.length === 0) {
        continue
      }
      for (const step of matchedSteps) {
        allSteps.push(step)
      }
    }
    return allSteps
  }

  getSelectedCampaignPriceDifference() {
    // take lowest value from campaigns
    let lowestCampaign: Campaign | undefined
    for (const campaign of this.configurator.value.campaigns.value) {
      if (isUndefined(lowestCampaign)) {
        lowestCampaign = campaign
        continue
      }
      if (campaign.discounted.value < lowestCampaign.discounted.value) {
        lowestCampaign = campaign
      }
    }

    let price: number | undefined
    if (isNotUndefined(lowestCampaign)) {
      const optionCampaignId = this.campaignId.value
      const optionCampaign = this.configurator.value.campaigns.value.find(
        (campaign) => campaign.id.value === optionCampaignId,
      )
      if (isNotUndefined(optionCampaign)) {
        price =
          optionCampaign.discounted.value - lowestCampaign.discounted.value

        if (price > 0) {
          return price
        }
      }
    }
    return undefined
  }

  getSelectionOverrideFilterParentStepName() {
    for (const filter of this.selectionOverride.value) {
      for (const [key, value] of Object.entries(filter.entries.value)) {
        return key
      }
    }
  }
}
